import { memo } from "react";
import type { ReactNode } from "react";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import RadioGroup from "@mui/material/RadioGroup";
import FormControlLabel from "@mui/material/FormControlLabel";
import Radio from "@mui/material/Radio";
import Checkbox from "@mui/material/Checkbox";
import FormGroup from "@mui/material/FormGroup";
import type { BoxProps } from "@mui/material/Box";
import type { CardProps } from "@mui/material/Card";

import { parseQuestionOptions } from "src-next/shared/utils/questionnaireOptions";
import type { QuestionPreviewItem } from "./types";

export type MaxWidthValue =
  | number
  | string
  | {
      xs?: number | string;
      sm?: number | string;
      md?: number | string;
      lg?: number | string;
    };

export interface QuestionnaireFormRendererProps
  extends Omit<BoxProps, "children"> {
  questions: QuestionPreviewItem[];
  /** Título do questionário, exibido acima das perguntas quando informado. */
  title?: string;
  /** Descrição do questionário, exibida abaixo do título quando informada. */
  description?: string;
  emptyMessage?: string;
  /** Mensagem quando múltipla escolha não tem opções definidas. */
  emptyOptionsMessage?: string;
  /**
   * Largura máxima do container (mobile-first).
   * - **Preview (card)**: omitir para herdar 100% do pai.
   * - **Página inteira**: usar `maxWidth={640}` ou `maxWidth={{ xs: "100%", sm: 640 }}`
   *   para mobile full width e largura limitada a partir de sm.
   * Quando definida, aplica width: "100%", maxWidth e mx: "auto" para centralizar.
   */
  maxWidth?: MaxWidthValue;
  /** Props repassados ao Card de cada pergunta (sx, elevation, etc.). */
  questionCardProps?: Omit<CardProps, "children">;
  /** Rótulo usado como fallback quando question.label está vazio (ex.: "Pergunta 1"). Padrão: "Pergunta". */
  questionLabel?: string;
  /**
   * Permite substituir o campo de resposta padrão por um renderer customizado.
   * Útil para integrar com React Hook Form sem duplicar o layout visual.
   */
  renderQuestionInput?: (
    question: QuestionPreviewItem,
    questionIndex: number,
  ) => ReactNode;
}

function getMaxWidthSx(value: MaxWidthValue): Record<string, unknown> {
  if (typeof value === "number" || typeof value === "string") {
    return {
      width: "100%",
      maxWidth: { xs: "100%", sm: value },
      mx: "auto",
    };
  }
  return {
    width: "100%",
    maxWidth: value,
    mx: "auto",
  };
}

const defaultCardSx = {
  borderRadius: 1,
  borderLeft: "3px solid",
  borderLeftColor: "primary.light",
} as const;

const questionIndexSx = {
  fontWeight: 600,
  color: "primary.main",
  letterSpacing: 0.5,
  display: "block",
  pb: 0.75,
  mb: 1,
  borderBottom: "1px solid",
  borderBottomColor: "divider",
} as const;

const wordBreakSx = {
  wordBreak: "break-word",
  overflowWrap: "break-word",
  minWidth: 0,
} as const;

const QuestionItem = memo(
  ({
    question,
    questionIndex,
    questionLabel,
    emptyOptionsMessage,
    cardProps,
    renderQuestionInput,
  }: {
    question: QuestionPreviewItem;
    questionIndex: number;
    questionLabel: string;
    emptyOptionsMessage: string;
    cardProps?: Omit<CardProps, "children">;
    renderQuestionInput?: (
      question: QuestionPreviewItem,
      questionIndex: number,
    ) => ReactNode;
  }) => {
    const isRequiredLabel = question.isRequired ? " *" : "";
    const options =
      question.type === "multipleChoice"
        ? parseQuestionOptions(question.optionsCsv)
        : [];

    const descricaoSx = {
      display: "block",
      mb: 1,
      fontSize: "0.75rem",
      color: "text.secondary",
      ...wordBreakSx,
    };

    const { sx: cardPropsSx, ...restCardProps } = cardProps ?? {};
    const cardSx = { ...defaultCardSx, ...cardPropsSx };
    const cardContentSx = { p: { xs: 1.5, sm: 2, md: 2 }, minWidth: 0 };

    const enunciadoHeader = (
      <Typography variant="subtitle2" sx={questionIndexSx}>
        {question.label?.trim() || `${questionLabel} ${questionIndex}`}
        {isRequiredLabel}
      </Typography>
    );

    if (renderQuestionInput) {
      return (
        <Card variant="outlined" sx={cardSx} {...restCardProps}>
          <CardContent sx={cardContentSx}>
            {enunciadoHeader}
            {question.helperText && (
              <Typography variant="caption" sx={descricaoSx}>
                {question.helperText}
              </Typography>
            )}
            {renderQuestionInput(question, questionIndex - 1)}
          </CardContent>
        </Card>
      );
    }

    if (question.type === "yesNo") {
      return (
        <Card variant="outlined" sx={cardSx} {...restCardProps}>
          <CardContent sx={cardContentSx}>
            {enunciadoHeader}
            {question.helperText && (
              <Typography variant="caption" sx={descricaoSx}>
                {question.helperText}
              </Typography>
            )}
            <RadioGroup row>
              <FormControlLabel value="yes" control={<Radio />} label="Sim" />
              <FormControlLabel value="no" control={<Radio />} label="Não" />
            </RadioGroup>
          </CardContent>
        </Card>
      );
    }

    if (question.type === "multipleChoice") {
      const shouldAllowMultipleAnswers = Boolean(question.allowMultipleAnswers);
      return (
        <Card variant="outlined" sx={cardSx} {...restCardProps}>
          <CardContent sx={cardContentSx}>
            {enunciadoHeader}
            {question.helperText && (
              <Typography variant="caption" sx={descricaoSx}>
                {question.helperText}
              </Typography>
            )}
            {shouldAllowMultipleAnswers ? (
              <FormGroup>
                {options.map((option) => (
                  <FormControlLabel
                    key={option}
                    control={<Checkbox />}
                    label={option}
                    sx={{ "& .MuiFormControlLabel-label": wordBreakSx }}
                  />
                ))}
                {options.length === 0 && (
                  <Typography variant="caption" color="text.disabled">
                    {emptyOptionsMessage}
                  </Typography>
                )}
              </FormGroup>
            ) : (
              <RadioGroup>
                {options.map((option) => (
                  <FormControlLabel
                    key={option}
                    value={option}
                    control={<Radio />}
                    label={option}
                    sx={{ "& .MuiFormControlLabel-label": wordBreakSx }}
                  />
                ))}
                {options.length === 0 && (
                  <Typography variant="caption" color="text.disabled">
                    {emptyOptionsMessage}
                  </Typography>
                )}
              </RadioGroup>
            )}
          </CardContent>
        </Card>
      );
    }

    return (
      <Card variant="outlined" sx={cardSx} {...restCardProps}>
        <CardContent sx={cardContentSx}>
          {enunciadoHeader}
          {question.helperText && (
            <Typography variant="caption" sx={descricaoSx}>
              {question.helperText}
            </Typography>
          )}
          <TextField
            label="Resposta"
            placeholder="Escreva aqui..."
            fullWidth
            multiline={question.type === "longText"}
            minRows={question.type === "longText" ? 5 : 1}
          />
        </CardContent>
      </Card>
    );
  },
);

QuestionItem.displayName = "QuestionItem";

const QuestionnaireFormRenderer = memo(
  ({
    questions,
    title,
    description,
    emptyMessage = "Nenhuma pergunta para exibir.",
    emptyOptionsMessage = "Nenhuma opção definida.",
    maxWidth,
    questionCardProps,
    questionLabel = "Pergunta",
    renderQuestionInput,
    sx,
    ...boxProps
  }: QuestionnaireFormRendererProps) => {
    const maxWidthSx = maxWidth !== undefined ? getMaxWidthSx(maxWidth) : {};
    const hasHeader = Boolean(title?.trim() || description?.trim());

    return (
      <Box
        sx={{
          overflow: "auto",
          ...maxWidthSx,
          ...sx,
        }}
        {...boxProps}
      >
        {hasHeader && (
          <Stack
            spacing={0.5}
            sx={{
              mb: { xs: 2, sm: 2.5, md: 3 },
              minWidth: 0,
              overflow: "hidden",
            }}
          >
            {title?.trim() && (
              <Typography
                variant="subtitle1"
                sx={{ fontWeight: 600, ...wordBreakSx }}
              >
                {title.trim()}
              </Typography>
            )}
            {description?.trim() && (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={wordBreakSx}
              >
                {description.trim()}
              </Typography>
            )}
          </Stack>
        )}
        {questions.length === 0 ? (
          <Typography variant="caption" color="text.secondary">
            {emptyMessage}
          </Typography>
        ) : (
          <Stack
            spacing={{ xs: 2, sm: 2.5, md: 3 }}
            sx={hasHeader ? undefined : { py: 0.5 }}
          >
            {questions.map((question, index) => (
              <QuestionItem
                key={question.id}
                question={question}
                questionIndex={index + 1}
                questionLabel={questionLabel}
                emptyOptionsMessage={emptyOptionsMessage}
                cardProps={questionCardProps}
                renderQuestionInput={renderQuestionInput}
              />
            ))}
          </Stack>
        )}
      </Box>
    );
  },
);

QuestionnaireFormRenderer.displayName = "QuestionnaireFormRenderer";

export default QuestionnaireFormRenderer;
